const mockHttpClient = {
  get: jest.fn(),
  post: jest.fn(),
  defaults: {},
};

jest.mock('axios', () => ({
  create: jest.fn(() => mockHttpClient),
}));

jest.mock('@/store', () => ({
  state: {
    app: {
      announcements: [],
    },
  },
}));

jest.mock('@/services/apiService', () => ({
  tenantUpdateSystemAnnouncementState: jest.fn(),
}));

describe('authentication actions verify', () => {
  let actions;

  beforeEach(() => {
    jest.clearAllMocks();
    jest.resetModules();
    // Re-require after module reset so action module picks fresh mocks
    actions = require('../actions').default;
  });

  it('sends X-API-Key when api key is provided', async () => {
    const commit = jest.fn();
    const apiKey = 'public-api-key';

    mockHttpClient.get
      .mockResolvedValueOnce({
        data: {
          user: { id: 7, properties: { tenantIdThemeSetting: 1 } },
          isAuthenticated: true,
          featureFlags: { flags: [] },
          tenantId: 11,
          access: [],
          primaryScopeIds: [],
          primaryTenantIds: [],
        },
      })
      .mockResolvedValueOnce({ data: [] });

    await actions.verify({ commit }, apiKey);

    expect(mockHttpClient.get).toHaveBeenNthCalledWith(1, '/auth/verify', {
      params: { domain: window.location.hostname },
      headers: { 'X-API-Key': apiKey },
    });
    expect(mockHttpClient.get).toHaveBeenNthCalledWith(2, '/user/own/announcements', {
      headers: { 'X-API-Key': apiKey },
    });
    expect(commit).toHaveBeenCalledWith('app/setFeatureFlags', [], { root: true });
    expect(commit).toHaveBeenCalledWith(
      'auth_success',
      expect.objectContaining({
        data: expect.objectContaining({ isApiKey: true }),
      })
    );
  });

  it('commits auth_success even when announcements request fails', async () => {
    const commit = jest.fn();

    mockHttpClient.get
      .mockResolvedValueOnce({
        data: {
          user: { id: 7, properties: { tenantIdThemeSetting: 1 } },
          isAuthenticated: true,
          featureFlags: { flags: [] },
          tenantId: 11,
          access: [],
          primaryScopeIds: [],
          primaryTenantIds: [],
        },
      })
      .mockRejectedValueOnce(new Error('Announcements request failed'));

    await actions.verify({ commit }, 'public-api-key');

    expect(commit).toHaveBeenCalledWith(
      'auth_success',
      expect.objectContaining({
        data: expect.objectContaining({
          user: expect.objectContaining({ id: 7 }),
          tenantId: 11,
        }),
      })
    );
  });

  it('does not send X-API-Key when no api key is provided', async () => {
    const commit = jest.fn();

    mockHttpClient.get
      .mockResolvedValueOnce({
        data: {
          user: { id: 9, properties: { tenantIdThemeSetting: 1 } },
          isAuthenticated: true,
          featureFlags: { flags: ['a'] },
          tenantId: 15,
          access: [],
          primaryScopeIds: [],
          primaryTenantIds: [],
        },
      })
      .mockResolvedValueOnce({ data: [] });

    await actions.verify({ commit });

    expect(mockHttpClient.get).toHaveBeenNthCalledWith(1, '/auth/verify', {
      params: { domain: window.location.hostname },
    });
    expect(mockHttpClient.get).toHaveBeenNthCalledWith(2, '/user/own/announcements', undefined);
    expect(commit).toHaveBeenCalledWith(
      'auth_success',
      expect.objectContaining({
        data: expect.objectContaining({ isApiKey: false }),
      })
    );
  });

  it('commits logout when verify request fails', async () => {
    const commit = jest.fn();
    const err = new Error('Unauthorized');

    mockHttpClient.get.mockRejectedValueOnce(err);

    await expect(actions.verify({ commit }, 'bad-key')).rejects.toThrow('Unauthorized');
    expect(commit).toHaveBeenCalledWith('logout');
  });

  it('commits logout when verify returns an invalid session', async () => {
    const commit = jest.fn();

    mockHttpClient.get.mockResolvedValueOnce({
      data: {
        user: null,
        isAuthenticated: false,
      },
    });

    await expect(actions.verify({ commit }, 'bad-key')).rejects.toThrow('Auth error');
    expect(commit).toHaveBeenCalledWith('logout');
  });

  it('does not commit logout when suppressLogoutOnError is enabled', async () => {
    const commit = jest.fn();
    const err = new Error('Network timeout');

    mockHttpClient.get.mockRejectedValueOnce(err);

    await expect(actions.verify({ commit }, { suppressLogoutOnError: true })).rejects.toThrow('Network timeout');
    expect(commit).not.toHaveBeenCalledWith('logout');
  });
});

describe('authentication actions login', () => {
  let actions;

  beforeEach(() => {
    jest.clearAllMocks();
    jest.resetModules();
    actions = require('../actions').default;
  });

  it('commits auth_success from login response even if verify fails', async () => {
    const commit = jest.fn();
    const dispatch = jest.fn().mockRejectedValue(new Error('verify timeout'));

    const loginResponse = {
      data: {
        user: { id: 7, properties: { tenantIdThemeSetting: 1 } },
        isAuthenticated: true,
        twoFactorSetup: {
          required: false,
          state: 'completed',
          completed: true,
          setupRequired: false,
        },
        access: [],
        primaryScopeIds: [],
        primaryTenantIds: [],
        tenantId: 11,
      },
    };

    mockHttpClient.post.mockResolvedValueOnce(loginResponse);

    const response = await actions.login.call(
      { state: { app: {} } },
      { commit, dispatch },
      { username: 'user', password: 'pw' }
    );

    expect(response).toBe(loginResponse);
    expect(commit).toHaveBeenNthCalledWith(1, 'auth_request');
    expect(commit).toHaveBeenNthCalledWith(
      2,
      'auth_success',
      expect.objectContaining({
        data: expect.objectContaining({
          user: expect.objectContaining({ id: 7 }),
          isApiKey: false,
        }),
      })
    );
    expect(dispatch).toHaveBeenCalledWith('verify', { suppressLogoutOnError: true });
    expect(commit).not.toHaveBeenCalledWith('logout');
  });

  it('commits logout when login request fails', async () => {
    const commit = jest.fn();
    const dispatch = jest.fn();
    const loginError = { response: { status: 401, data: { error: 'AUTH_LOGIN_PASSWORD_MISMATCH' } } };

    mockHttpClient.post.mockRejectedValueOnce(loginError);

    await expect(
      actions.login.call({ state: { app: {} } }, { commit, dispatch }, { username: 'user', password: 'wrong' })
    ).rejects.toEqual(loginError.response);
    expect(dispatch).not.toHaveBeenCalled();
    expect(commit).toHaveBeenCalledWith('logout');
  });
});
